import { z } from 'zod';

// 02.6-docs.md paso a′ — schemas para las formas reales de request/response del dominio `docs`,
// verificadas contra el DTO y los casos de uso del backend (`backend/src/docs/domain/DocDTO.js`,
// `backend/src/docs/application/{list-docs,get-doc,create-doc,update-doc,delete-doc,
// hard-delete-docs,update-doc-media-visibility,update-doc-cover-visibility}/*.js`,
// `backend/src/shared/storage/toggleMediaAccess.js`), no inventadas desde cómo el frontend las
// consume hoy.
//
// `.parse()` en todos lados (nunca `.safeParse()`), `z.looseObject` en todo — mismo criterio que
// `features/calendar/schemas.js`/`features/databases/schemas.js`: el backend puede agregar
// campos sin avisar, y un allowlist estricto rompería en silencio el día que eso pase.

const nullish = (schema) => schema.nullish();
const id = z.union([z.string(), z.number()]);

// ── Doc (`DocDTO`'s FIELDS — todos opcionales salvo `id`; `content`/`doc_filter_info`/
// `doc_permissions`/`perm_*`/`tiip` se dejan `z.unknown()` porque su forma real varía según el
// endpoint (JSONB crudo vs. ya resuelto/parseado) y ningún consumidor de este slice depende de
// una forma estricta ahí — mismo criterio que `CalendarEventSchema`'s `recurrence`/`attendees`
// y `CustomValueResponseSchema`'s `value`) ─────────────────────────────────────────────────────
export const DocSchema = z.looseObject({
    id: nullish(id),
    title: nullish(z.string()),
    text: nullish(z.string()),
    description: nullish(z.string()),
    parent_id: nullish(id),
    order_index: nullish(z.number()),
    icon: nullish(z.string()),
    active_status: nullish(z.string()),
    table_data_name: nullish(z.string()),
    origin_row_uid: nullish(id),
    origin_table_uid: nullish(id),
    doc_filter_info: z.unknown().nullish(),
    doc_permissions: z.unknown().nullish(),
    perm_ver: z.unknown().nullish(),
    perm_edit: z.unknown().nullish(),
    perm_tem_ver: z.unknown().nullish(),
    perm_tem_edit: z.unknown().nullish(),
    perm_comment: z.unknown().nullish(),
    perm_temp_comment: z.unknown().nullish(),
    is_locked: nullish(z.boolean()),
    created_at: nullish(z.string()),
    updated_at: nullish(z.string()),
    created_by: nullish(id),
    space_id: nullish(id),
    author_nick: nullish(z.string()),
    author_img: nullish(z.string()),
    last_date: nullish(z.string()),
    content: z.unknown().nullish(),
    tiip: z.unknown().nullish(),
    cover_img: nullish(z.string()),
    cover_visibility: nullish(z.string()),
    comment_count: nullish(z.number()),
    has_unread_mentions: nullish(z.boolean()),
    // Integración main→dev, epic D2 — `GetDoc.can_edit` (permiso de edición del solicitante,
    // para el modo solo-lectura del editor); `space_name`/`space_icon` de `findAllTree`
    // (agrupación del árbol por espacio en la vista `/all`).
    can_edit: nullish(z.boolean()),
    space_name: nullish(z.string()),
    space_icon: nullish(z.string()),
});

// ── Historial de versiones (`"DocVersion"`, migración 151 — integración main→dev, epic D2) ──
// `DocVersionListItemSchema` = lo que devuelve `ListDocVersions` (SIN `content`);
// `DocVersionSchema` = lo que devuelve `GetDocVersion` (CON `content` ya resuelto a string).
export const DocVersionListItemSchema = z.looseObject({
    id: nullish(id),
    doc_id: nullish(id),
    title: nullish(z.string()),
    version_name: nullish(z.string()),
    is_manual: nullish(z.boolean()),
    created_by: nullish(id),
    author_nick: nullish(z.string()),
    author_img: nullish(z.string()),
    created_at: nullish(z.string()),
    word_count: nullish(z.number()),
});

export const DocVersionSchema = DocVersionListItemSchema.extend({
    content: z.unknown().nullish(),
});

export const DocVersionListResponseSchema = z.looseObject({
    success: z.boolean(),
    versions: nullish(z.array(DocVersionListItemSchema)),
    error: nullish(z.string()),
});

export const DocVersionResponseSchema = z.looseObject({
    success: z.boolean(),
    version: nullish(DocVersionSchema),
    error: nullish(z.string()),
});

export const CreateDocVersionResponseSchema = z.looseObject({
    success: z.boolean(),
    version: nullish(DocVersionListItemSchema),
    error: nullish(z.string()),
});

export const RestoreDocVersionResponseSchema = z.looseObject({
    success: z.boolean(),
    doc: nullish(DocSchema),
    restoredVersionId: nullish(id),
    message: nullish(z.string()),
    error: nullish(z.string()),
});

// ── Respuesta de `list` (`ListDocs.execute`) ────────────────────────────────────────────────
export const DocsListResponseSchema = z.looseObject({
    success: z.boolean(),
    docs: nullish(z.array(DocSchema)),
    error: nullish(z.string()),
});

// ── Respuesta de `getById`/`create`/`update` (`GetDoc`/`CreateDoc`/`UpdateDoc.execute`) --
// `message` (no `error`) es lo que manda el "doc no encontrado"/403 de acceso-denegado de
// `UpdateDoc._checkPermission` (ambos campos opcionales acá a propósito, mismo tratamiento que
// `EventResponseSchema` de calendar le dio a su propio 403). ───────────────────────────────────
export const DocResponseSchema = z.looseObject({
    success: z.boolean(),
    doc: nullish(DocSchema),
    error: nullish(z.string()),
    message: nullish(z.string()),
});

// ── Respuesta de `remove` (`DeleteDoc.execute`) ─────────────────────────────────────────────
export const RemoveDocResponseSchema = z.looseObject({
    success: z.boolean(),
    id: nullish(id),
    error: nullish(z.string()),
    message: nullish(z.string()),
});

// ── Respuesta de `hardDelete` (`HardDeleteDocs.execute`) ────────────────────────────────────
export const HardDeleteDocsResponseSchema = z.looseObject({
    success: z.boolean(),
    count: nullish(z.number()),
    error: nullish(z.string()),
    message: nullish(z.string()),
});

// ── Respuesta de `toggleMediaVisibility`/`toggleCoverVisibility`
// (`toggleMediaAccess`, compartido por 8 dominios) ───────────────────────────────────────────
export const ToggleVisibilityResponseSchema = z.looseObject({
    success: z.boolean(),
    access: nullish(z.string()),
    url: nullish(z.string()),
    error: nullish(z.string()),
});

// ── Payload de `create` (`POST /api/docs`) — parcial real, todos los campos opcionales salvo
// `title` (`CreateDoc.execute` desestructura con default solo si vienen, y el propio
// `docsApi`/call-sites de este slice siempre mandan `title`). `autor` es el nombre real del
// campo que el backend espera para `created_by` (el router lo fuerza a `req.user.id` de todos
// modos, ver `docRouter.js`'s comentario de auditoría 2026-07-12) ──────────────────────────────
export const CreateDocPayloadSchema = z.looseObject({
    title: z.string(),
    autor: nullish(id),
    text: nullish(z.string()),
    description: nullish(z.string()),
    parent_id: nullish(id),
    order_index: nullish(z.number()),
    icon: nullish(z.string()),
    type: nullish(z.string()),
    table_data_name: nullish(z.string()),
    content: z.unknown().nullish(),
    origin_row_uid: nullish(id),
    origin_table_uid: nullish(id),
    space_id: nullish(id),
});

// ── Payload de `update` (`PUT /api/docs/:id`) — `UpdateDoc.execute` recibe PARCIALES reales
// hoy (un solo campo a la vez es el caso común: `{icon}`, `{title}`, `{is_locked}`,
// `{active_status}`, `{cover_img}`, `{content}`), ningún campo requerido, mismo criterio que
// `EventPayloadSchema` de calendar. ──────────────────────────────────────────────────────────
export const UpdateDocPayloadSchema = z.looseObject({
    title: nullish(z.string()),
    text: nullish(z.string()),
    description: nullish(z.string()),
    parent_id: nullish(id),
    order_index: nullish(z.number()),
    icon: nullish(z.string()),
    active_status: nullish(z.string()),
    is_locked: nullish(z.boolean()),
    content: z.unknown().nullish(),
    cover_img: z.unknown().nullish(),
    tiip: z.unknown().nullish(),
    doc_filter_info: z.unknown().nullish(),
    doc_permissions: z.unknown().nullish(),
});

// ── Respuesta de `uploadEmbed` (`docsApi.uploadEmbed`, no un endpoint HTTP -- envuelve
// `uploadFile()` que sube directo a GCS) -- unión discriminada por `success`, NO un
// `z.looseObject` único: las dos formas no comparten campos (`url` vs. `error`), mecanizar el
// patrón plano de arriba haría fallar el `.parse()` de cualquiera de las dos ramas. Encontrado
// por el pase de `/refine-plans` (§4 del sub-plan) -- el único caso de esta forma en todo el
// slice. ─────────────────────────────────────────────────────────────────────────────────────
export const UploadEmbedResponseSchema = z.discriminatedUnion('success', [
    z.looseObject({ success: z.literal(true), url: z.string() }),
    z.looseObject({ success: z.literal(false), error: z.string() }),
]);
